const WORK_START_HOUR = 8

function toNumber(value, fallback = 0) {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : fallback
}

export function remainingWork(process, productionUnitM2) {
    const qtyPp = toNumber(process.qty_pp, 1) || 1
    const merma = toNumber(process.merma)
    if (productionUnitM2) {
        const target = toNumber(process.product_mts2) * qtyPp
        const done = toNumber(process.total_mts2)
        return Math.max(0, target - done)
    }
    const target = toNumber(process.product_qty) * qtyPp + merma
    const done = toNumber(process.total_qty)
    return Math.max(0, target - done)
}

export function isProcessComplete(process, productionUnitM2) {
    return remainingWork(process, productionUnitM2) <= 0.001
}

function parseDate(value) {
    if (!value) return null
    const d = value instanceof Date ? value : new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

function startOfDay(date) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

export function processRisk(endDate, deliveryDate) {
    const end = parseDate(endDate)
    const due = parseDate(deliveryDate)
    if (!end || !due) return 'ok'
    const endDay = startOfDay(end)
    const dueDay = startOfDay(due)
    if (endDay > dueDay) return 'late'
    if (endDay === dueDay) return 'risk'
    return 'ok'
}

function isWorkingDay(date, saturday, sunday) {
    const day = date.getDay()
    if (day === 6 && !saturday) return false
    if (day === 0 && !sunday) return false
    return true
}

function snapToWorkingTime(date, saturday, sunday, hoursPerDay) {
    const endHour = WORK_START_HOUR + hoursPerDay
    let cursor = new Date(date)

    for (let i = 0; i < 400; i++) {
        if (!isWorkingDay(cursor, saturday, sunday)) {
            cursor.setDate(cursor.getDate() + 1)
            cursor.setHours(WORK_START_HOUR, 0, 0, 0)
            continue
        }
        const hour = cursor.getHours() + cursor.getMinutes() / 60 + cursor.getSeconds() / 3600
        if (hour < WORK_START_HOUR) {
            cursor.setHours(WORK_START_HOUR, 0, 0, 0)
            return cursor
        }
        if (hour >= endHour) {
            cursor.setDate(cursor.getDate() + 1)
            cursor.setHours(WORK_START_HOUR, 0, 0, 0)
            continue
        }
        return cursor
    }
    return cursor
}

function addWorkingHours(start, hours, hoursPerDay, saturday, sunday) {
    let remaining = hours
    let cursor = new Date(start)
    const endHour = WORK_START_HOUR + hoursPerDay
    let guard = 0

    while (remaining > 0.0001 && guard < 10000) {
        guard += 1
        cursor = snapToWorkingTime(cursor, saturday, sunday, hoursPerDay)
        const nowHour = cursor.getHours() + cursor.getMinutes() / 60 + cursor.getSeconds() / 3600
        const leftToday = Math.max(0, endHour - nowHour)
        const take = Math.min(leftToday, remaining)
        cursor = new Date(cursor.getTime() + take * 3600 * 1000)
        remaining -= take
        if (remaining > 0.0001) {
            cursor.setDate(cursor.getDate() + 1)
            cursor.setHours(WORK_START_HOUR, 0, 0, 0)
        }
    }
    return cursor
}

export function orderQueue(machine) {
    const orders = machine.process_orders || []
    const sequence = machine.secuence || []
    const byProcess = {}
    orders.forEach((process) => {
        byProcess[process.process] = process
    })

    const queue = []
    sequence.forEach((id) => {
        if (byProcess[id]) {
            queue.push(byProcess[id])
            delete byProcess[id]
        }
    })

    Object.values(byProcess)
        .sort((a, b) => toNumber(a.sort) - toNumber(b.sort))
        .forEach((process) => queue.push(process))

    return queue
}

export function estimateMachineQueue(machine, now = new Date()) {
    const capacity = toNumber(machine.capacity)
    const hoursPerDay = Math.max(1, toNumber(machine.hours_per_day, 8))
    const productionUnitM2 = !!machine.production_unit_m2
    const saturday = !!machine.saturday
    const sunday = !!machine.sunday
    const source = Array.isArray(machine.queue)
        ? machine.queue
        : orderQueue(machine)

    let cursor = snapToWorkingTime(new Date(now), saturday, sunday, hoursPerDay)

    source.forEach((process) => {
        const remaining = remainingWork(process, productionUnitM2)
        const estimatedHours = capacity > 0 ? remaining / capacity : 0
        const start = new Date(cursor)
        const done = remaining <= 0.001

        if (done || estimatedHours <= 0) {
            process.start_date = start.toISOString()
            process.end_date = start.toISOString()
            process.estimated_hours = 0
            process.remaining = 0
            process.done = true
            process.risk = processRisk(process.end_date, process.delivery_date)
            return
        }

        cursor = addWorkingHours(cursor, estimatedHours, hoursPerDay, saturday, sunday)
        process.start_date = start.toISOString()
        process.end_date = cursor.toISOString()
        process.estimated_hours = estimatedHours
        process.remaining = remaining
        process.done = false
        process.risk = processRisk(process.end_date, process.delivery_date)
    })

    return source
}

export function estimatePlant(machines, now = new Date()) {
    return (machines || []).map((machine) => {
        const queue = estimateMachineQueue(machine, now)
        machine.queue = queue
        machine.process_orders = queue
        machine.secuence = queue.map((process) => process.process)
        return machine
    })
}

export function matchesSearch(process, query) {
    if (!query || !String(query).trim()) return true
    const needle = String(query).toLowerCase().trim()
    return [
        process.work_order,
        process.customer_name,
        process.product_name,
        process.product_sku,
        process.name,
        process.equipment_assigned,
    ].some((value) => String(value || '').toLowerCase().includes(needle))
}

export function matchesRisk(process, riskFilter) {
    if (!riskFilter || riskFilter === 'all') return true
    return process.risk === riskFilter
}

export function matchesEquipment(process, equipment) {
    if (!equipment || equipment === 'Todos') return true
    const wanted = String(equipment).trim().toLowerCase().replace(/\s+/g, '')
    const assigned = String(process.equipment_assigned || '').trim().toLowerCase().replace(/\s+/g, '')
    return assigned === wanted
}

export function buildSavePayload(machines) {
    return (machines || []).map((machine) => ({
        id: machine.id,
        process_orders: (machine.queue || []).map((process) => ({
            process: process.process,
        })),
    }))
}

export function plantKpis(machines) {
    let pendingM2 = 0
    let pendingQty = 0
    let late = 0
    let loadHours = 0
    let machinesWithQueue = 0

    ;(machines || []).forEach((machine) => {
        const queue = machine.queue || []
        if (queue.length) machinesWithQueue += 1
        queue.forEach((process) => {
            if (process.done) return
            if (machine.production_unit_m2) {
                pendingM2 += toNumber(process.remaining)
            } else {
                pendingQty += toNumber(process.remaining)
            }
            loadHours += toNumber(process.estimated_hours)
            if (process.risk === 'late') late += 1
        })
    })

    return {
        pendingM2,
        pendingQty,
        late,
        loadHours,
        machinesWithQueue,
        machineCount: (machines || []).length,
    }
}

export function uniqueEquipmentNames(machines) {
    const names = new Set()
    ;(machines || []).forEach((machine) => {
        ;(machine.equipments || []).forEach((equipment) => {
            if (equipment && equipment.name) names.add(equipment.name)
        })
        ;(machine.queue || []).forEach((process) => {
            if (process.equipment_assigned) names.add(process.equipment_assigned)
        })
    })
    return ['Todos', ...Array.from(names).sort()]
}

export function enabledEquipments(machine) {
    return (machine.equipments || []).filter((equipment) => equipment && equipment.enabled !== false)
}

export function planLoadDistribution(machine) {
    const equipments = enabledEquipments(machine)
    const jobs = (machine.queue || []).filter((job) => !job.done)
    if (equipments.length < 2) {
        return { skipped: true, reason: 'need-machines', bins: [], jobs: jobs.length, machines: equipments.length }
    }
    if (!jobs.length) {
        return { skipped: true, reason: 'no-jobs', bins: [], jobs: 0, machines: equipments.length }
    }

    const bins = equipments.map((equipment) => ({
        equipment,
        hours: 0,
        jobs: [],
    }))

    jobs.forEach((job) => {
        const hours = Math.max(0.01, toNumber(job.estimated_hours, 0.01))
        let target = bins[0]
        bins.forEach((bin) => {
            if (bin.hours < target.hours) target = bin
        })
        target.jobs.push(job)
        target.hours += hours
    })

    return {
        skipped: false,
        bins,
        jobs: jobs.length,
        machines: equipments.length,
    }
}
